import {
  Apple,
  ArrowRight,
  Github,
  GitBranch,
  Monitor,
  Smartphone,
  Terminal,
} from "lucide-react";
import { AppHeader } from "@/components/app-header";
import { AppFooter } from "@/components/app-footer";
import { LocaleLink } from "@/components/locale-link";
import { GitHubStarButton } from "@/components/github-star-button";
import { siteGitHubUrl } from "@/config/site";
import { DEMO_URL, SIGN_UP_LEDGER } from "@/config/product-links";
import { productUrl } from "@/lib/product-attribution";
import { useTranslation } from "@/common/providers/i18n-provider";
import "@/styles/marketing-theme.css";

/** Shared presentational view for both locale route trees. */
export function DownloadPage() {
  const { t } = useTranslation();

  return (
    <div className="download-page min-h-screen bg-background text-foreground">
      <AppHeader />
      <main id="main">
        <section className="relative bg-muted/30">
          <div
            className="pointer-events-none absolute inset-0 opacity-55"
            style={{
              backgroundImage:
                "linear-gradient(to right, color-mix(in oklch, var(--border) 62%, transparent) 1px, transparent 1px), linear-gradient(to bottom, color-mix(in oklch, var(--border) 62%, transparent) 1px, transparent 1px)",
              backgroundSize: "72px 72px",
              maskImage:
                "linear-gradient(to bottom, black 0%, black 55%, transparent 100%)",
            }}
            aria-hidden="true"
          />
          <div className="relative mx-auto max-w-[1180px] px-4 pb-12 pt-16 sm:px-6 sm:pb-16 sm:pt-20 lg:pt-24">
            <div className="max-w-3xl">
              <h1 className="font-display-brand text-balance text-5xl leading-[0.98] tracking-[-0.035em] text-foreground sm:text-6xl lg:text-7xl">
                {t("download.hero.title")}
              </h1>
              <p className="mt-6 max-w-2xl text-pretty text-lg leading-relaxed text-muted-foreground sm:text-xl">
                {t("download.hero.subtitle")}
              </p>
            </div>
          </div>
        </section>

        <section className="relative bg-muted/30 pb-10 sm:pb-14 lg:pb-20">
          <div className="mx-auto max-w-[1180px] space-y-6 px-4 sm:px-6">
            {/* Web */}
            <article className="grid overflow-hidden rounded-[1.5rem] border border-border bg-card shadow-sm md:min-h-[470px] md:grid-cols-12 md:rounded-[2rem]">
              <div className="order-2 flex flex-col justify-center px-7 py-10 sm:px-10 md:order-1 md:col-span-7 md:px-14 lg:px-20">
                <span className="mb-6 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Monitor className="size-6" aria-hidden="true" />
                </span>
                <h2 className="font-display-brand text-3xl leading-tight text-foreground sm:text-4xl">
                  {t("cloud.cloudNativePlatform")}
                </h2>
                <p className="mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
                  {t("cloud.accessYourAccountingDataFromAnywhere")}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                  <a
                    href={productUrl(SIGN_UP_LEDGER, "download")}
                    className="btn bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  >
                    {t("common.getStartedFree")}
                  </a>
                  <a
                    href={productUrl(DEMO_URL, "download")}
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                  >
                    {t("homepage.tryDemo")}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
              <div className="relative order-1 min-h-[360px] overflow-hidden bg-primary/10 md:order-2 md:col-span-5 md:min-h-full">
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 30% 18%, color-mix(in oklch, var(--primary) 24%, transparent), transparent 36%), radial-gradient(circle at 85% 84%, color-mix(in oklch, var(--foreground) 8%, transparent), transparent 38%)",
                  }}
                  aria-hidden="true"
                />
                <div className="absolute left-8 top-10 w-[158%] overflow-hidden rounded-2xl border border-white/10 bg-[#0c100d] p-1.5 shadow-[0_28px_64px_-24px_rgb(0_0_0/0.65)] md:left-10 md:top-14">
                  <div className="flex h-7 items-center gap-1.5 px-2">
                    <span
                      className="size-2 rounded-full bg-destructive/75"
                      aria-hidden="true"
                    />
                    <span
                      className="size-2 rounded-full bg-amber-400/80"
                      aria-hidden="true"
                    />
                    <span
                      className="size-2 rounded-full bg-primary/80"
                      aria-hidden="true"
                    />
                  </div>
                  <img
                    src="/img/ledger/overview-example.webp"
                    alt=""
                    width={1600}
                    height={1000}
                    loading="eager"
                    fetchPriority="high"
                    decoding="async"
                    className="block aspect-[16/10] w-full rounded-xl object-cover"
                  />
                </div>
              </div>
            </article>

            {/* iOS */}
            <article className="grid overflow-hidden rounded-[1.5rem] border border-border bg-card shadow-sm md:min-h-[470px] md:grid-cols-12 md:rounded-[2rem]">
              <div className="order-2 flex flex-col justify-center px-7 py-10 sm:px-10 md:order-1 md:col-span-7 md:px-14 lg:px-20">
                <span className="mb-6 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Apple className="size-6" aria-hidden="true" />
                </span>
                <h2 className="font-display-brand text-3xl leading-tight text-foreground sm:text-4xl">
                  {t("ios.beancountForIos")}
                </h2>
                <p className="mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
                  {t("ios.takeControlOfYourFinancesOn")}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                  <a
                    href="/ios"
                    className="btn bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  >
                    {t("download.downloadNow")}
                  </a>
                  <LocaleLink
                    to="/beancount-ios"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                  >
                    {t("common.learnMore")}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </LocaleLink>
                </div>
              </div>
              <div className="relative order-1 min-h-[360px] overflow-hidden bg-primary/10 md:order-2 md:col-span-5 md:min-h-full">
                <div
                  className="absolute inset-0 opacity-60"
                  style={{
                    backgroundImage:
                      "radial-gradient(circle at 25% 20%, color-mix(in oklch, var(--primary) 30%, transparent), transparent 34%), radial-gradient(circle at 80% 85%, color-mix(in oklch, var(--primary) 18%, transparent), transparent 38%)",
                  }}
                  aria-hidden="true"
                />
                <div className="absolute inset-x-0 top-8 mx-auto w-[56%] max-w-[260px] overflow-hidden rounded-[2.25rem] border-[7px] border-[#20251e] bg-[#11150f] shadow-[0_28px_64px_-24px_rgb(0_0_0/0.65)] md:top-12 md:w-[62%]">
                  <img
                    src="/img/download/mobile-reports.webp"
                    alt=""
                    width={1206}
                    height={2622}
                    loading="lazy"
                    decoding="async"
                    className="block h-auto w-full"
                  />
                </div>
              </div>
            </article>

            {/* Android */}
            <article className="grid overflow-hidden rounded-[1.5rem] border border-border bg-card shadow-sm md:min-h-[470px] md:grid-cols-12 md:rounded-[2rem]">
              <div className="order-2 flex flex-col justify-center px-7 py-10 sm:px-10 md:order-1 md:col-span-7 md:px-14 lg:px-20">
                <span className="mb-6 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <Smartphone className="size-6" aria-hidden="true" />
                </span>
                <h2 className="font-display-brand text-3xl leading-tight text-foreground sm:text-4xl">
                  {t("android.beancountForAndroid")}
                </h2>
                <p className="mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
                  {t("android.takeControlOfYourFinancesOn")}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                  <a
                    href="/android"
                    className="btn bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  >
                    {t("download.downloadNow")}
                  </a>
                  <LocaleLink
                    to="/beancount-android"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                  >
                    {t("common.learnMore")}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </LocaleLink>
                </div>
              </div>
              <div className="relative order-1 min-h-[360px] overflow-hidden bg-[color-mix(in_oklch,var(--primary)_7%,var(--muted))] md:order-2 md:col-span-5 md:min-h-full">
                <div
                  className="absolute inset-0 opacity-70"
                  style={{
                    backgroundImage:
                      "linear-gradient(135deg, color-mix(in oklch, var(--primary) 16%, transparent), transparent 48%), radial-gradient(circle at 70% 18%, color-mix(in oklch, var(--foreground) 8%, transparent), transparent 34%)",
                  }}
                  aria-hidden="true"
                />
                <div className="absolute inset-x-0 top-8 mx-auto w-[56%] max-w-[260px] overflow-hidden rounded-[2.25rem] border-[7px] border-[#20251e] bg-[#11150f] shadow-[0_28px_64px_-24px_rgb(0_0_0/0.65)] md:top-12 md:w-[62%]">
                  <img
                    src="/img/download/mobile-add-transaction.webp"
                    alt=""
                    width={1206}
                    height={2622}
                    loading="lazy"
                    decoding="async"
                    className="block h-auto w-full"
                  />
                </div>
              </div>
            </article>

            {/* Plain text + Git */}
            <article className="grid overflow-hidden rounded-[1.5rem] border border-border bg-card shadow-sm md:min-h-[470px] md:grid-cols-12 md:rounded-[2rem]">
              <div className="order-2 flex flex-col justify-center px-7 py-10 sm:px-10 md:order-1 md:col-span-7 md:px-14 lg:px-20">
                <span className="mb-6 flex size-12 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                  <GitBranch className="size-6" aria-hidden="true" />
                </span>
                <h2 className="font-display-brand text-3xl leading-tight text-foreground sm:text-4xl">
                  {t("homepage.gitNativeVersionControl")}
                </h2>
                <p className="mt-4 max-w-lg text-lg leading-relaxed text-muted-foreground">
                  {t("homepage.everyFinancialChangeIsAGit")}
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-5">
                  <LocaleLink
                    to="/git"
                    className="btn bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  >
                    {t("common.learnMore")}
                  </LocaleLink>
                  <a
                    href={siteGitHubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1.5 text-sm font-semibold text-primary hover:underline"
                  >
                    {t("common.viewOnGithub")}
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </a>
                </div>
              </div>
              <div className="relative order-1 min-h-[360px] overflow-hidden bg-[color-mix(in_oklch,var(--primary)_6%,var(--muted))] md:order-2 md:col-span-5 md:min-h-full">
                <img
                  src="/img/ledger/journal-example.webp"
                  alt=""
                  width={1600}
                  height={1000}
                  loading="lazy"
                  decoding="async"
                  className="absolute inset-0 h-full w-full object-cover object-left opacity-65"
                />
                <div className="absolute inset-x-6 bottom-8 overflow-hidden rounded-2xl border border-white/10 bg-[#0c100d]/95 shadow-2xl backdrop-blur [--primary:var(--marketing-accent,var(--brand))] md:inset-x-8 md:bottom-10">
                  <div className="flex items-center gap-2 border-b border-white/10 px-4 py-3 text-primary">
                    <Terminal className="size-4" aria-hidden="true" />
                    <span className="font-mono text-xs">ledger.git</span>
                  </div>
                  <pre className="overflow-hidden px-5 py-5 font-mono text-xs leading-6 text-white/80 sm:text-sm">
                    <code>
                      <span className="text-primary">$</span> git clone
                      {"\n"}
                      <span className="text-primary">$</span> $EDITOR main.bean
                      {"\n"}
                      <span className="text-primary">$</span> git commit -am
                      books
                      {"\n"}
                      <span className="text-primary">$</span> git push
                    </code>
                  </pre>
                </div>
              </div>
            </article>
          </div>
        </section>

        <section className="relative border-t border-border/60 bg-muted/30">
          <div className="mx-auto max-w-[1180px] px-4 py-8 sm:px-6 sm:py-12 md:py-16">
            <div className="relative overflow-hidden rounded-[1.5rem] border border-primary/20 bg-[linear-gradient(135deg,color-mix(in_oklch,var(--primary)_13%,var(--card)),var(--card)_64%)] px-7 py-12 sm:px-12 md:rounded-[2rem] md:px-16 md:py-16">
              <div
                className="pointer-events-none absolute -right-16 -top-24 size-72 rounded-full bg-primary/10 blur-3xl"
                aria-hidden="true"
              />
              <div className="relative max-w-3xl">
                <h2 className="font-display-brand text-3xl text-foreground sm:text-4xl">
                  {t("common.openSourceAndTransparent")}
                </h2>
                <p className="mt-4 max-w-2xl text-lg leading-relaxed text-muted-foreground">
                  {t("download.source.blurb")}
                </p>
                <div className="mt-8 flex flex-col items-start gap-4 sm:flex-row sm:items-center">
                  <a
                    href={siteGitHubUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="btn bg-primary text-primary-foreground hover:bg-primary/90 hover:text-primary-foreground"
                  >
                    <Github className="mr-2 size-4" aria-hidden="true" />
                    {t("common.viewOnGithub")}
                  </a>
                  <GitHubStarButton />
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>
      <AppFooter />
    </div>
  );
}
